import { useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router';
import { nanoid } from '@reduxjs/toolkit';
import { downloadFromUrl } from 'helper/downloadFile';
import { setFileDownloadControls } from 'helper/fileDownloadControls';
import sendErrorToSentry from 'helper/sendErrorToSentry';
import { StudyActionRef } from 'helper/flux/myStudyRow';
import handleAtuthorizationErrors from 'helper/tokenError';
import { ResponseError } from 'services/api/base';
import { usePersonalizedDownloadLinkMutation } from 'services/api/personalized';
import {
  downloadDismissed,
  downloadFailed,
  downloadPreparing,
  downloadStarted,
  selectFileDownload,
} from 'redux/reducers/fileDownloadsReducer';
import { zipDownloadErrorMessage } from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';

export const NO_LINK_MESSAGE = 'O servidor não devolveu o link do arquivo. Tente novamente em instantes.';
export const OPENED_IN_NEW_TAB_MESSAGE = 'O arquivo foi aberto em uma nova aba. Se nada aparecer, libere os pop-ups deste site.';

/** Mutation que pede o link do zip (`{ caso_id }` → `{ data: { link } }`), uma por produto. */
export type DownloadLinkMutationHook = typeof usePersonalizedDownloadLinkMutation;

/**
 * Baixar o zip de um estudo (Personalizado ou Upload): pede o link ao servidor, abre em nova aba
 * (ou cai para o download por link se o pop-up for bloqueado) e mostra o card de progresso.
 * Lê só id, nome, última resposta e erro interno (`toStudyActionRef`, #1994).
 */
export default function useStudyDownload(
  study: StudyActionRef,
  useLinkMutation: DownloadLinkMutationHook,
) {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const [requestLink, { isLoading }] = useLinkMutation();
  const disabled = study.dt_last_response === null || study.ck_internal_error;

  const download = () => {
    const id = nanoid();

    setFileDownloadControls(id, { retry: download });
    dispatch(downloadPreparing({
      id,
      nome: study.ds_nome_estudo,
      mensagem: 'Preparando o arquivo no servidor…',
    }));

    requestLink({ caso_id: study.id })
      .unwrap()
      .then((response) => {
        // Outro download tomou o card enquanto este esperava o servidor: não abre nada por cima.
        if (selectFileDownload(store.getState())?.id !== id) return;
        const link = response?.data?.link;
        if (!link) {
          dispatch(downloadFailed({ id, mensagem: NO_LINK_MESSAGE }));
          return;
        }
        // O link é aberto depois de um await, já fora do gesto do clique (o useEffect antigo
        // também abria assim): o bloqueador de pop-up pode barrar e devolver null. Nesse caso
        // cai para o download nativo por link na própria página, que não depende de pop-up.
        // Sem 'noopener' na feature string (com ele o window.open sempre devolve null e não
        // daria para detectar o bloqueio); o opener é cortado à mão logo em seguida.
        const opened = window.open(link, '_blank');
        if (opened) {
          opened.opener = null;
          dispatch(downloadStarted({
            id, tamanhoBytes: null, mensagem: OPENED_IN_NEW_TAB_MESSAGE,
          }));
          return;
        }
        downloadFromUrl(link);
        dispatch(downloadStarted({ id, tamanhoBytes: null }));
      })
      .catch((error: ResponseError) => {
        // Sessão vencida/sem permissão seguem o fluxo de sempre; o resto vira o card de erro
        // e o usuário continua na página (antes o erro era ignorado e nada acontecia).
        if (handleAtuthorizationErrors(error, navigate)) {
          dispatch(downloadDismissed(id));
          return;
        }
        sendErrorToSentry(error);
        dispatch(downloadFailed({ id, mensagem: zipDownloadErrorMessage(error) }));
      });
  };

  return { download, isLoading, disabled };
}
